import React, { useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import {Helmet} from "react-helmet"

import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'

import Window from 'components/dialog/Window'
import SideMenu from 'components/layout/SideMenu'
import AppBarMenu from 'components/layout/AppBarMenu'
import Snackbar from 'components/system/Snackbar'
import GlobalLoading from 'components/system/GlobalLoading'
import Image from 'components/widgets/Image'

import authSelectors from 'store/selectors/auth'
import networkSelectors from 'store/selectors/network'
import uiSelectors from 'store/selectors/ui'
import projectSelectors from 'store/selectors/project'
import projectActions from 'store/modules/project'
import bookingSelectors from 'store/selectors/booking'
import routerSelectors from 'store/selectors/router'
import bookingActions from 'store/modules/booking'
import routerActions from 'store/modules/router'
import authActions from 'store/modules/auth'

const useStyles = makeStyles(theme => ({
  toolbar: {
    paddingLeft: '0px',
    paddingRight: '0px',
  },
  title: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  logo: {
    width: '50px',
    marginRight: theme.spacing(2),
  },
  headerOffset: {
    width: theme.spacing(2),
  },
  userEmail: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  projectSelect: {
    marginLeft: theme.spacing(2),
    color: 'inherit',
    fontSize: '0.9em',
    '& .MuiSelect-icon': {
      color: 'inherit',
    },
    '&:before': {
      borderBottom: 'none',
    }
  }
}))

const Layout = ({
  children,
}) => {
  const theme = useTheme()
  const largeScreen = useMediaQuery(theme.breakpoints.up('sm'))
  const classes = useStyles()
  const dispatch = useDispatch()

  const user = useSelector(authSelectors.data)
  const tokenerror = useSelector(authSelectors.tokenerror)
  const route = useSelector(routerSelectors.route)
  const globalLoading = useSelector(networkSelectors.globalLoading)

  const projectId = useSelector(projectSelectors.currentProjectId)
  const project = useSelector(projectSelectors.currentProject)
  const currentBookingform = useSelector(bookingSelectors.currentBookingform)
  const projects = useSelector(projectSelectors.projects)
  const title = useSelector(uiSelectors.title)
  const logo = useSelector(uiSelectors.logo)
  const MENU = useSelector(uiSelectors.menu)
  const showMenus = useSelector(uiSelectors.showMenus)
  const reservationError = useSelector(bookingSelectors.reservationError)

  const onCloseTokenError = useCallback(() => {
    dispatch(authActions.logout())
  }, [])

  const onViewProject = useCallback((id) => {
    dispatch(projectActions.switchProject(id))
  }, [])

  const onRestartBooking = useCallback(() => {
    dispatch(bookingActions.clearReservationError())
  }, [])

  const pageTitles = useMemo(() => {
    const projectName = project ? project.name : 'Nowtakemybooking.com'
    const bookingFormName = currentBookingform ? currentBookingform.name : ''
    const all = [projectName, bookingFormName].filter(s => s).join(' : ')

    return {
      head: all,
      appbar: largeScreen ? all : bookingFormName,
    }
  }, [
    project,
    currentBookingform,
    largeScreen,
  ])

  const logoElement = logo ?
    (
      <Image
        className={ classes.logo }
        data={ logo }
      />
    ) : null

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{ pageTitles.head }</title>
      </Helmet>
      <AppBar position="sticky">
        <Toolbar className={ classes.toolbar }>
          {
            showMenus ? (
              <SideMenu 
                items={ MENU }
              />
            ) : (
              <div className={ classes.headerOffset }></div>
            )
          }
          { logoElement }
          <Typography 
            variant="h6" 
            color="inherit" 
            className={ classes.title }
          >
            { pageTitles.appbar }
          </Typography>
          {
            user && route.name.indexOf('admin.') == 0 && (
              <Typography 
                className={ classes.userEmail }
                variant="caption" 
                color="inherit" 
              >
                { user.email }
              </Typography>
            )
          }
          {
            user && route.name.indexOf('admin.') == 0 && projects.length > 1 && (
              <Select
                value={ projectId }
                className={ classes.projectSelect }
                onChange={ (e) => onViewProject(e.target.value) }
              >
                {
                  projects.map((project, i) => {
                    return (
                      <MenuItem
                        key={ i }
                        value={ project.id }
                      >
                        { project.name }
                      </MenuItem>
                    )
                  })
                }
              </Select>
            )
          }
          {
            showMenus && (
              <AppBarMenu
                items={ MENU }
                user={ user }
              />
            )
          }
        </Toolbar>
      </AppBar>
      { children }
      <GlobalLoading
        loading={ globalLoading }
      />
      <Snackbar />
      {
        reservationError && (
          <Window
            open
            title="Session Expired"
            size="sm"
            withCancel
            cancelTitle="Restart Booking"
            onCancel={ onRestartBooking }
          >
            <Typography
              color="error"
            >
              { reservationError }
            </Typography>
          </Window>
        )
      }
      {
        tokenerror && (
          <Window
            open
            size="sm"
            title="Login Error"
            onSubmit={ onCloseTokenError }
            submitTitle="Continue"
          >
            <Typography>
              { tokenerror }
            </Typography>
          </Window>
        )
      }
    </>
  )
}

export default Layout